import React from 'react';

interface MetricCardsProps {
    activeCoupons: number;
    activeCouponsLimit: number;
    conversionRate: number; // 0-100
    totalViews: number;
    totalRedemptions: number;
    showActiveCouponsLimit?: boolean;
    bestPerforming: {
        couponName: string;
        conversionRate: number; // 0-100
        redemptions: number;
    };
    redemptionBreakdown: {
        inApp: number;
        inStore: number;
    };
}

export const MetricCards: React.FC<MetricCardsProps> = ({
    activeCoupons,
    activeCouponsLimit,
    conversionRate,
    totalViews,
    totalRedemptions,
    showActiveCouponsLimit = true,
    bestPerforming,
    redemptionBreakdown,
}) => {
    const orangeGradient = `linear-gradient(0deg, white 0%, white 100%), linear-gradient(162deg, white 0%, rgba(255, 247.11, 236.96, 0.50) 50%, rgba(254.03, 231.96, 199.97, 0.30) 100%)`;
    const cyanGradient = `linear-gradient(0deg, white 0%, white 100%), linear-gradient(162deg, white 0%, rgba(233.01, 246.97, 249.97, 0.20) 50%, rgba(184.03, 229.96, 239.96, 0.30) 100%)`;

    const CardShell: React.FC<{
        title: string;
        gradient: string;
        icon: React.ReactNode;
        children: React.ReactNode;
    }> = ({ title, gradient, icon, children }) => (
        <div
            className="flex-1 p-[1.6px] rounded-[12.13px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] dark:outline-gray-700 flex flex-col gap-[10px] min-w-0 bg-white dark:bg-gray-800"
        >
            <div className="self-stretch pt-[15px] pb-[6.5px] px-[19.5px] flex justify-between items-center">
                <div className="flex flex-col">
                    <div
                        className="text-[#364153] dark:text-gray-300 text-[12px] font-semibold leading-[16.25px]"
                        style={{ fontFamily: 'Nunito' }}
                    >
                        {title}
                    </div>
                </div>
                <div className="p-[6.5px] bg-[#F2FCFA] dark:bg-gray-700 rounded-[8.13px]">{icon}</div>
            </div>
            {children}
        </div>
    );

    const iconStroke = 'hsl(var(--primary))';

    return (
        <div className="self-stretch flex flex-col lg:flex-row justify-center items-stretch gap-[19.5px]">
            <CardShell
                title="Active Coupons"
                gradient={orangeGradient}
                icon={
                    // Figma icon (also matches `src/assets/icons/gift-icon.svg`)
                    <svg width="16.25" height="16.25" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_active_coupons)">
                            <path
                                d="M1.35449 6.09359C1.89321 6.09359 2.40987 6.30759 2.7908 6.68853C3.17174 7.06946 3.38574 7.58612 3.38574 8.12484C3.38574 8.66356 3.17174 9.18022 2.7908 9.56115C2.40987 9.94208 1.89321 10.1561 1.35449 10.1561V11.5103C1.35449 11.8694 1.49716 12.2138 1.75112 12.4678C2.00507 12.7218 2.34951 12.8644 2.70866 12.8644H13.542C13.9011 12.8644 14.2456 12.7218 14.4995 12.4678C14.7535 12.2138 14.8962 11.8694 14.8962 11.5103V10.1561C14.3574 10.1561 13.8408 9.94208 13.4598 9.56115C13.0789 9.18022 12.8649 8.66356 12.8649 8.12484C12.8649 7.58612 13.0789 7.06946 13.4598 6.68853C13.8408 6.30759 14.3574 6.09359 14.8962 6.09359V4.73942C14.8962 4.38027 14.7535 4.03584 14.4995 3.78188C14.2456 3.52792 13.9011 3.38525 13.542 3.38525H2.70866C2.34951 3.38525 2.00507 3.52792 1.75112 3.78188C1.49716 4.03584 1.35449 4.38027 1.35449 4.73942V6.09359Z"
                                stroke={iconStroke}
                                strokeWidth="1.35"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path d="M8.80176 3.38525V4.73942" stroke={iconStroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8.80176 11.5103V12.8644" stroke={iconStroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8.80176 7.44775V8.80192" stroke={iconStroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_active_coupons">
                                <rect width="16.25" height="16.25" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                }
            >
                <div className="self-stretch pb-[15px] px-[19.5px] flex flex-col gap-[3.2px]">
                    <div className="text-[#364153] dark:text-white text-[24.4px] font-bold leading-[29.25px]" style={{ fontFamily: 'Nunito' }}>
                        {showActiveCouponsLimit ? `${activeCoupons}/${activeCouponsLimit}` : activeCoupons}
                    </div>
                    <div className="text-[#4A5565] dark:text-gray-400 text-[11px] font-normal leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        Currently available
                    </div>
                </div>
            </CardShell>

            <CardShell
                title="Overall Conversion Rate"
                gradient={cyanGradient}
                icon={
                    // Figma icon (matches `src/assets/icons/total-redemptions-icon.svg`)
                    <svg width="16.25" height="16.25" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M1.35449 6.09359C1.89321 6.09359 2.40987 6.30759 2.7908 6.68853C3.17174 7.06946 3.38574 7.58612 3.38574 8.12484C3.38574 8.66356 3.17174 9.18022 2.7908 9.56115C2.40987 9.94208 1.89321 10.1561 1.35449 10.1561V11.5103C1.35449 11.8694 1.49716 12.2138 1.75112 12.4678C2.00507 12.7218 2.34951 12.8644 2.70866 12.8644H13.542C13.9011 12.8644 14.2456 12.7218 14.4995 12.4678C14.7535 12.2138 14.8962 11.8694 14.8962 11.5103V10.1561C14.3574 10.1561 13.8408 9.94208 13.4598 9.56115C13.0789 9.18022 12.8649 8.66356 12.8649 8.12484C12.8649 7.58612 13.0789 7.06946 13.4598 6.68853C13.8408 6.30759 14.3574 6.09359 14.8962 6.09359V4.73942C14.8962 4.38027 14.7535 4.03584 14.4995 3.78188C14.2456 3.52792 13.9011 3.38525 13.542 3.38525H2.70866C2.34951 3.38525 2.00507 3.52792 1.75112 3.78188C1.49716 4.03584 1.35449 4.38027 1.35449 4.73942V6.09359Z"
                            stroke={iconStroke}
                            strokeWidth="1.35"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path d="M6.09375 8.12516L7.44792 9.47933L10.1562 6.771" stroke={iconStroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                }
            >
                <div className="self-stretch pb-[15px] px-[19.5px] flex flex-col gap-[3.2px]">
                    <div className="text-[#364153] dark:text-white text-[24.4px] font-bold leading-[29.25px]" style={{ fontFamily: 'Nunito' }}>
                        {Math.round(conversionRate)}%
                    </div>
                    <div className="text-[#4A5565] dark:text-gray-400 text-[11px] font-normal leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        {totalViews} views → {totalRedemptions} redemptions
                    </div>
                </div>
            </CardShell>

            <CardShell
                title="Best Performing"
                gradient={orangeGradient}
                icon={
                    // Figma icon (node-id: 21034:1957)
                    <svg width="16.25" height="16.25" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_best_performing)">
                            <path
                                d="M7.80298 1.55367C7.83265 1.49372 7.87848 1.44326 7.93531 1.40798C7.99214 1.3727 8.0577 1.354 8.12459 1.354C8.19148 1.354 8.25704 1.3727 8.31387 1.40798C8.3707 1.44326 8.41653 1.49372 8.44621 1.55367L10.0103 4.72174C10.1133 4.93026 10.2654 5.11067 10.4535 5.24747C10.6416 5.38427 10.8601 5.47338 11.0902 5.50716L14.588 6.01904C14.6543 6.02864 14.7166 6.0566 14.7678 6.09974C14.819 6.14289 14.8571 6.19951 14.8778 6.2632C14.8985 6.32688 14.901 6.39509 14.885 6.46011C14.869 6.52513 14.835 6.58437 14.7871 6.63112L12.2575 9.09435C12.0907 9.25692 11.9659 9.45759 11.8938 9.6791C11.8217 9.9006 11.8046 10.1363 11.8438 10.3659L12.441 13.8461C12.4527 13.9124 12.4455 13.9806 12.4203 14.0429C12.3951 14.1053 12.3529 14.1593 12.2985 14.1989C12.2441 14.2384 12.1796 14.2618 12.1125 14.2665C12.0454 14.2712 11.9783 14.2569 11.919 14.2253L8.79219 12.5813C8.58617 12.4731 8.35695 12.4166 8.12425 12.4166C7.89155 12.4166 7.66233 12.4731 7.45631 12.5813L4.33022 14.2253C4.27086 14.2567 4.20387 14.2708 4.13688 14.2661C4.06988 14.2613 4.00557 14.2379 3.95125 14.1984C3.89693 14.1588 3.85479 14.1049 3.82962 14.0426C3.80445 13.9804 3.79726 13.9123 3.80886 13.8461L4.40537 10.3666C4.44478 10.1369 4.4277 9.90102 4.35563 9.67938C4.28356 9.45774 4.15865 9.25695 3.99167 9.09435L1.46209 6.6318C1.41374 6.5851 1.37948 6.52576 1.36321 6.46054C1.34694 6.39533 1.34931 6.32685 1.37006 6.26292C1.39081 6.19898 1.42909 6.14216 1.48056 6.09892C1.53202 6.05568 1.5946 6.02777 1.66115 6.01836L5.15829 5.50716C5.38867 5.47365 5.60746 5.38465 5.79582 5.24783C5.98418 5.11101 6.13647 4.93047 6.23959 4.72174L7.80298 1.55367Z"
                                stroke={iconStroke}
                                strokeWidth="1.35"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_best_performing">
                                <rect width="16.25" height="16.25" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                }
            >
                <div className="self-stretch pb-[15px] px-[19.5px] flex flex-col gap-[3.2px] min-w-0">
                    <div className="text-[#364153] dark:text-white text-[24.4px] font-bold leading-[29.25px] truncate" style={{ fontFamily: 'Nunito' }}>
                        {bestPerforming.couponName}
                    </div>
                    <div className="text-[#4A5565] dark:text-gray-400 text-[11px] font-normal leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        {Math.round(bestPerforming.conversionRate)}% conversion • {bestPerforming.redemptions} redemptions
                    </div>
                </div>
            </CardShell>

            <CardShell
                title="Redemption Breakdown"
                gradient={orangeGradient}
                icon={
                    <svg width="16.25" height="16.25" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M10.8337 6.771C10.8337 7.48929 10.5483 8.17817 10.0404 8.68608C9.5325 9.19399 8.84362 9.47933 8.12533 9.47933C7.40703 9.47933 6.71816 9.19399 6.21024 8.68608C5.70233 8.17817 5.41699 7.48929 5.41699 6.771"
                            stroke={iconStroke}
                            strokeWidth="1.35"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path d="M2.10059 4.08545H14.1486" stroke={iconStroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                        <path
                            d="M2.30208 3.70145C2.12628 3.93585 2.03125 4.22095 2.03125 4.51395V13.5415C2.03125 13.9007 2.17392 14.2451 2.42788 14.499C2.68183 14.753 3.02627 14.8957 3.38542 14.8957H12.8646C13.2237 14.8957 13.5682 14.753 13.8221 14.499C14.0761 14.2451 14.2187 13.9007 14.2187 13.5415V4.51395C14.2187 4.22095 14.1237 3.93585 13.9479 3.70145L12.5937 1.89567C12.4676 1.72749 12.3041 1.59098 12.116 1.49697C11.928 1.40295 11.7206 1.354 11.5104 1.354H4.73958C4.52936 1.354 4.32201 1.40295 4.13398 1.49697C3.94595 1.59098 3.78239 1.72749 3.65625 1.89567L2.30208 3.70145Z"
                            stroke={iconStroke}
                            strokeWidth="1.35"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                }
            >
                <div className="self-stretch pb-[15px] px-[19.5px] flex">
                    <div className="flex-1 border-r border-[#E6E6E6] dark:border-gray-600 flex flex-col justify-center items-center gap-[3px]">
                        <div className="text-[#364153] dark:text-white text-[24.4px] font-bold leading-[29.25px]" style={{ fontFamily: 'Nunito' }}>
                            {redemptionBreakdown.inApp}
                        </div>
                        <div className="text-[#4A5565] dark:text-gray-400 text-[11px] font-normal leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                            In-App
                        </div>
                    </div>
                    <div className="flex-1 flex flex-col justify-center items-center gap-[3px]">
                        <div className="text-[#364153] dark:text-white text-[24.4px] font-bold leading-[29.25px]" style={{ fontFamily: 'Nunito' }}>
                            {redemptionBreakdown.inStore}
                        </div>
                        <div className="text-[#4A5565] dark:text-gray-400 text-[11px] font-normal leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                            In-store
                        </div>
                    </div>
                </div>
            </CardShell>
        </div>
    );
};

